<script setup>
import {computed, inject} from 'vue';
import {usePortalContentAndDesignStore} from "../../../../../../stores/partners/portalContentAndDesignStore";
import EditableTextField from "../../../../portal-editable-fields/EditableTextField.vue";
import EditableTextEditorField from "../../../../portal-editable-fields/EditableTextEditorField.vue";
import EditableImageField from "../../../../portal-editable-fields/EditableImageField.vue";
import EditableResourceTextEditorField
    from "../../../../portal-editable-fields/resources/EditableResourceTextEditorField.vue";

const props = defineProps({
    resource: {
        type: Object,
        required: true,
    },
});

const portalContentAndDesignStore = usePortalContentAndDesignStore();
const fileUrls = inject('fileUrls');

// Computed properties for styling
const getTextColorClass = computed(() => {
    const textColor = portalContentAndDesignStore.portalLayoutSettings?.public_page_content_text_color || 'gray-800';
    return `text-${textColor}`;
});

const getBorderColorClass = computed(() => {
    const borderColor = portalContentAndDesignStore.portalLayoutSettings?.content_border_color || 'gray-200';
    return `border-${borderColor}`;
});

const getCornerClass = computed(() => {
    const corners = portalContentAndDesignStore.portalLayoutSettings?.referral_portal_content_layout_corners || 'rounded-xl';
    return corners === 'none' ? 'rounded-sm' : corners;
});

const getGridCols = computed(() => {
    const rewards = props.resource?.contents?.rewards || [];
    const count = rewards.length;
    if (count > 3) return 'md:grid-cols-3';
    if (count === 3) return 'md:grid-cols-3';
    if (count === 2) return 'md:grid-cols-2';
    return 'md:grid-cols-1';
});

const hasMainImage = computed(() => {
    return props.resource?.main_image?.url;
});

const imageSizeClass = computed(() => {
    // Backwards compatibility: check for old retina resize setting
    if (props.resource?.main_image_size === undefined && props.resource?.main_image_retina_resize !== undefined) {
        return props.resource.main_image_retina_resize ? 'max-w-8' : 'max-w-16';
    }
    // New size setting
    const size = props.resource?.main_image_size || 'medium';
    switch (size) {
        case 'small':
        case '@2x': return 'max-w-8';
        case 'large': return 'max-w-24';
        default: return 'max-w-16'; // medium
    }
});

const displayInColumns = computed(() => {
    return props.resource?.display_blocks_in_columns || false;
});

const alignmentClass = computed(() => {
    switch (props.resource.alignment) {
        case 'center': return 'justify-center text-center';
        case 'right': return 'justify-end text-right';
        default: return 'justify-start text-left';
    }
});

const headerLayoutClass = computed(() => {
    const position = props.resource.main_image_position || 'left';
    const alignment = props.resource.alignment || 'left';

    if (position === 'top') {
        if (alignment === 'center') return 'flex flex-col items-center gap-4';
        if (alignment === 'right') return 'flex flex-col items-end gap-4';
        return 'flex flex-col items-start gap-4';
    }

    if (alignment === 'center') return 'flex flex-col items-center gap-4';
    return 'flex items-center gap-6';
});
</script>

<template>
    <div>
        <!-- Main title and description section -->
        <div v-if="hasMainImage" :class="[headerLayoutClass, alignmentClass]">
            <!-- Main image -->
            <div :class="imageSizeClass">
                <editable-image-field
                    v-model:image-path="resource.main_image.url"
                    img-name="main_image"
                    title="Main Image"
                    :image-upload-url="fileUrls?.upload"
                    :image-delete-url="fileUrls?.delete"
                    :img-class="imageSizeClass"
                />
            </div>

            <!-- Title and description -->
            <div class="space-y-1.5">
                <editable-text-field
                    v-if="resource.title"
                    text-type="h3"
                    :class="['text-xl md:text-2xl font-semibold', portalContentAndDesignStore.getPageContentTextColorClass]"
                    v-model="resource.title"
                />
                <editable-resource-text-editor-field
                    v-if="resource.description"
                    :class="[
                        'partnero-page-content-section text-base md:text-lg text-opacity-80 space-y-1',
                        portalContentAndDesignStore.getPageContentTextColorClass
                    ]"
                    :resource="resource"
                    resource-field="description"
                />
            </div>
        </div>

        <!-- Title and description without image -->
        <div v-else :class="alignmentClass">
            <editable-text-field
                v-if="resource.title"
                text-type="h2"
                class="text-lg md:text-2xl font-semibold pb-2"
                :class="getTextColorClass"
                v-model="resource.title"
            />
            <editable-text-editor-field
                v-if="resource.description"
                class="partnero-page-content-section text-md md:text-lg text-opacity-80 space-y-1"
                :class="getTextColorClass"
                v-model="resource.description"
            />
        </div>

        <!-- Rewards grid -->
        <div
            v-if="resource.contents?.rewards && resource.contents.rewards.length > 0"
            class="grid gap-6"
            :class="[
                (resource.title || resource.description) ? 'mt-4 md:mt-6' : '',
                displayInColumns ? `md:gap-12 ${getGridCols}` : ''
            ]"
        >
            <div
                v-for="(reward, index) in resource.contents.rewards"
                :key="`reward-${index}`"
                class="border p-2 md:p-4 flex justify-start items-center gap-4"
                :class="[getBorderColorClass, getCornerClass]"
            >
                <!-- Reward icon -->
                <div
                    class="flex flex-shrink-0 justify-center items-center w-10 h-10 text-md bg-gray-50"
                    :class="[getCornerClass, getTextColorClass]"
                >
                    <span class="icons d-inline-flex">
                        <svg width="20" height="20" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                            <path fill="none" d="M0 0h24v24H0z"></path>
                            <path d="M14.5 2a3.5 3.5 0 0 1 3.163 5.001L21 7a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1h-1v8a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-8H3a1 1 0 0 1-1-1V8a1 1 0 0 1 1-1l3.337.001a3.5 3.5 0 0 1 5.664-3.95A3.48 3.48 0 0 1 14.5 2zM18 13H6v7h12v-7zm2-4H4v2h16V9zM9.5 4a1.5 1.5 0 0 0-.144 2.993L9.5 7H11V5.5a1.5 1.5 0 0 0-1.356-1.493L9.5 4zm5 0l-.144.007a1.5 1.5 0 0 0-1.35 1.349L13 5.5V7h1.5l.144-.007a1.5 1.5 0 0 0 0-2.986L14.5 4z"></path>
                        </svg>
                    </span>
                </div>

                <!-- Reward content -->
                <div>
                    <editable-text-field
                        v-if="reward.name"
                        text-type="div"
                        class="text-sm md:text-base font-semibold"
                        :class="getTextColorClass"
                        v-model="reward.name"
                    />
                    <editable-text-field
                        v-if="reward.description"
                        text-type="div"
                        class="text-sm md:text-base text-opacity-80"
                        :class="getTextColorClass"
                        v-model="reward.description"
                    />
                </div>
            </div>
        </div>

        <!-- Placeholder when no rewards -->
        <div v-else class="text-center py-8 text-gray-500">
            <p>No rewards configured yet.</p>
        </div>
    </div>
</template>

<style scoped>
.partnero-page-content-section {
    /* Preserve any specific styling for content sections */
}
</style>
